import React, { useState, useEffect, useRef } from "react";
import { Spinner } from "reactstrap";
import * as echarts from "echarts";
import FeatherIcon from "feather-icons-react";

const card = {
  background:   "var(--vz-secondary-bg, #fff)",
  border:       "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow:    "0 1px 4px rgba(0,0,0,0.06)",
  marginBottom: 12,
  overflow:     "hidden",
};

const cardHeader = {
  padding:        "14px 18px",
  borderBottom:   "1px solid var(--vz-border-color)",
  display:        "flex",
  alignItems:     "center",
  justifyContent: "space-between",
};

const AgentStatusCharts = () => {
  const [agents,  setAgents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [history, setHistory] = useState([]);

  const pieRef  = useRef(null);
  const lineRef = useRef(null);
  const pieInst  = useRef(null);
  const lineInst = useRef(null);

  const creds     = () => ({ apiUrl: localStorage.getItem("flaskApiUrl"), apiKey: localStorage.getItem("flaskApiKey") });
  const authFetch = async (url) => {
    const { apiUrl, apiKey } = creds();
    if (!apiUrl || !apiKey) throw new Error("Credentials not configured");
    const r = await fetch(`${apiUrl}${url}`, { headers: { "Content-Type": "application/json", "X-API-Key": apiKey } });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || "Request failed");
    return d;
  };

  const fetchAgents = async () => {
    setLoading(true); setError(null);
    try {
      const data = await authFetch("/api/wazuh/agents");
      if (data?.success) {
        const list  = (data.agents || []).filter(a => a.id !== "000");
        const now   = new Date();
        const label = `${now.getHours()}:${String(now.getMinutes()).padStart(2, "0")}`;
        const online  = list.filter(a => ["active","online"].some(s => (a.status||"").toLowerCase().includes(s))).length;
        const offline = list.filter(a => ["disconnect","offline"].some(s => (a.status||"").toLowerCase().includes(s))).length;
        setAgents(list);
        setHistory(prev => [...prev, { time: label, online, offline, total: list.length }].slice(-15));
      }
    } catch (e) { setError(e.message); }
    finally     { setLoading(false); }
  };

  useEffect(() => {
    fetchAgents();
    const t = setInterval(fetchAgents, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const stats = {
    total:   agents.length,
    online:  agents.filter(a => ["active","online"].some(s    => (a.status||"").toLowerCase().includes(s))).length,
    offline: agents.filter(a => ["disconnect","offline"].some(s => (a.status||"").toLowerCase().includes(s))).length,
    never:   agents.filter(a => (a.status||"").toLowerCase().includes("never")).length,
  };

  // ── Theme tokens (resolved at render time) ──────────────────────────────
  const getTokens = () => {
    const root = getComputedStyle(document.documentElement);
    return {
      textColor:   root.getPropertyValue("--vz-body-color").trim()   || "#adb5bd",
      borderColor: root.getPropertyValue("--vz-border-color").trim() || "#e9ebec",
      cardBg:      root.getPropertyValue("--vz-card-bg").trim()      || "#ffffff",
    };
  };

  // Fixed chart colours (same in both modes — vivid enough for both)
  const C_ONLINE  = "#22c55e";
  const C_OFFLINE = "#f97316";
  const C_NEVER   = "#94a3b8";

  // ── Pie chart ────────────────────────────────────────────────────────────
  const initPie = () => {
    if (!pieRef.current) return;
    if (pieInst.current) pieInst.current.dispose();

    const chart = echarts.init(pieRef.current);
    pieInst.current = chart;
    const { textColor, borderColor, cardBg } = getTokens();

    chart.setOption({
      tooltip: {
        trigger: "item",
        formatter: "{b}: {c} ({d}%)",
        backgroundColor: cardBg,
        borderColor,
        borderWidth: 1,
        textStyle: { color: textColor },
      },
      legend: {
        orient: "horizontal", bottom: 0,
        textStyle: { color: textColor, fontSize: 12 },
      },
      color: [C_ONLINE, C_OFFLINE, C_NEVER],
      series: [{
        name: "Agent Status", type: "pie",
        radius: ["38%", "58%"], center: ["50%", "48%"],
        avoidLabelOverlap: true,
        itemStyle: { borderRadius: 4, borderWidth: 2, borderColor: "transparent" },
        label:    { show: true, formatter: "{b}\n{c} ({d}%)", fontSize: 11, color: textColor },
        labelLine: { show: true, length: 14, length2: 8 },
        emphasis:  { label: { fontSize: 13, fontWeight: "bold" } },
        data: [
          { value: stats.online,  name: "Online" },
          { value: stats.offline, name: "Offline" },
          { value: stats.never,   name: "Never Connected" },
        ],
      }],
      textStyle: { fontFamily: "inherit" },
    });

    const onResize = () => chart.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); };
  };

  // ── Line chart ───────────────────────────────────────────────────────────
  const initLine = () => {
    if (!lineRef.current || history.length < 2) return;
    if (lineInst.current) lineInst.current.dispose();

    const chart = echarts.init(lineRef.current);
    lineInst.current = chart;
    const { textColor, borderColor, cardBg } = getTokens();
    const splitColor = borderColor;

    chart.setOption({
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        backgroundColor: cardBg,
        borderColor,
        borderWidth: 1,
        textStyle: { color: textColor },
      },
      legend: {
        data: ["Online","Offline","Total"], top: 8,
        textStyle: { color: textColor, fontSize: 12 },
      },
      grid: { left: "5%", right: "5%", bottom: "8%", top: "20%", containLabel: true },
      xAxis: {
        type: "category", boundaryGap: false,
        data: history.map(h => h.time),
        axisLabel: { color: textColor, rotate: 40, fontSize: 10 },
        axisLine:  { lineStyle: { color: borderColor } },
      },
      yAxis: {
        type: "value", minInterval: 1,
        axisLabel: { color: textColor, fontSize: 11 },
        splitLine: { lineStyle: { color: splitColor, type: "dashed" } },
      },
      series: [
        {
          name: "Online", type: "line", smooth: true,
          data: history.map(h => h.online),
          symbol: "circle", symbolSize: 6,
          lineStyle: { color: C_ONLINE, width: 2.5 },
          itemStyle: { color: C_ONLINE },
          areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "rgba(34,197,94,.25)" }, { offset: 1, color: "rgba(34,197,94,.03)" }] } },
        },
        {
          name: "Offline", type: "line", smooth: true,
          data: history.map(h => h.offline),
          symbol: "circle", symbolSize: 6,
          lineStyle: { color: C_OFFLINE, width: 2.5 },
          itemStyle: { color: C_OFFLINE },
          areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "rgba(249,115,22,.25)" }, { offset: 1, color: "rgba(249,115,22,.03)" }] } },
        },
        {
          name: "Total", type: "line", smooth: true,
          data: history.map(h => h.total),
          symbol: "diamond", symbolSize: 7,
          lineStyle: { color: C_NEVER, width: 2, type: "dashed" },
          itemStyle: { color: C_NEVER },
        },
      ],
      textStyle: { fontFamily: "inherit" },
    });

    const onResize = () => chart.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); };
  };

  // ── Init / re-init charts when data or theme changes ────────────────────
  useEffect(() => {
    if (!loading && !error) {
      const cleanPie  = initPie();
      const cleanLine = initLine();
      return () => { cleanPie?.(); cleanLine?.(); };
    }
    // eslint-disable-next-line
  }, [agents, history, loading, error]);

  // ── Re-init on theme toggle (same MutationObserver pattern as CostOfIncident) ──
  useEffect(() => {
    const observer = new MutationObserver(() => {
      initPie();
      initLine();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-layout-mode"],
    });
    return () => observer.disconnect();
    // eslint-disable-next-line
  }, [agents, history]);

  // ── Cleanup on unmount ───────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      pieInst.current?.dispose();
      lineInst.current?.dispose();
    };
  }, []);

  // ── Render guards ────────────────────────────────────────────────────────
  if (loading && agents.length === 0) return (
    <div style={{ ...card, padding: "48px 24px", textAlign: "center" }}>
      <Spinner color="primary" />
      <p style={{ marginTop: 8, color: "var(--vz-secondary-color)", fontSize: 13 }}>Loading analytics...</p>
    </div>
  );

  if (error) return (
    <div style={{ padding: "10px 16px", background: "rgba(var(--vz-danger-rgb),.1)", border: "1px solid rgba(var(--vz-danger-rgb),.3)", borderRadius: 8, fontSize: 13, color: "var(--vz-danger)", marginBottom: 12 }}>
      <FeatherIcon icon="alert-circle" size={14} className="me-2" />{error}
    </div>
  );

  return (
    <>
      {/* ── Pie chart ──────────────────────────────────────── */}
      <div style={card}>
        <div style={cardHeader}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "var(--vz-body-color)" }}>Status Distribution</span>
          <span style={{ fontSize: 11, color: "var(--vz-secondary-color)" }}>{stats.total} agents</span>
        </div>
        <div style={{ padding: "4px 18px 18px" }}>
          <div ref={pieRef} style={{ height: 300 }} />

          {/* Summary row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", borderTop: "1px solid var(--vz-border-color)", paddingTop: 14, marginTop: 4 }}>
            {[
              { label: "Online",  value: stats.online,  icon: "check-circle", color: C_ONLINE  },
              { label: "Offline", value: stats.offline, icon: "x-circle",     color: C_OFFLINE },
              { label: "Health",  value: `${stats.total > 0 ? Math.round((stats.online / stats.total) * 100) : 0}%`, icon: "activity", color: "var(--vz-primary)" },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: "center", padding: "0 8px", borderRight: i < 2 ? "1px solid var(--vz-border-color)" : "none" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, marginBottom: 2 }}>
                  <FeatherIcon icon={s.icon} size={13} style={{ color: s.color }} />
                  <span style={{ fontWeight: 700, fontSize: 16, color: s.color }}>{s.value}</span>
                </div>
                <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", margin: 0 }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Line chart ─────────────────────────────────────── */}
      <div style={card}>
        <div style={cardHeader}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "var(--vz-body-color)" }}>Connection Trend</span>
          <span style={{ fontSize: 11, color: "var(--vz-secondary-color)" }}>
            <FeatherIcon icon="refresh-cw" size={11} className="me-1" />60s refresh
          </span>
        </div>
        <div style={{ padding: "4px 18px 18px" }}>
          {history.length >= 2 ? (
            <div ref={lineRef} style={{ height: 300 }} />
          ) : (
            <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "var(--vz-secondary-color)" }}>
              <FeatherIcon icon="clock" size={36} className="d-block mb-3" style={{ opacity: .35 }} />
              <p style={{ fontSize: 13, margin: 0 }}>Collecting data...</p>
              <p style={{ fontSize: 12, margin: "4px 0 0", opacity: .7 }}>Chart appears after 2+ data points (~60s)</p>
            </div>
          )}

          {/* Summary row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: "1px solid var(--vz-border-color)", paddingTop: 12, marginTop: 4 }}>
            <div style={{ textAlign: "center" }}>
              <span style={{ fontWeight: 700, color: "var(--vz-body-color)" }}>{history.length}</span>
              <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", margin: 0 }}>Data Points</p>
            </div>
            <div style={{ textAlign: "center", borderLeft: "1px solid var(--vz-border-color)" }}>
              <span style={{ fontWeight: 700, color: "var(--vz-body-color)" }}>60s</span>
              <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", margin: 0 }}>Auto-refresh</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AgentStatusCharts;